import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { v4 as uuid } from 'uuid';

import { Space, Button, Select, Input, Form, Modal, InputNumber, Flex, Tooltip, Tag, Divider, notification } from 'antd';

import { PlusOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';

import FormHeader from '@controls/form-header/form-header';

import { securityRestriction } from '@src/extensions/notification';
import { exception, warning, error } from '@extensions/notification';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { userLoaded } from '@store/actions';
import { serverFetch } from '@src/server';

import { IUserSession } from '@src/entities/user-session';
import { IInboundCarrier } from '@src/entities/inbound-carrier';
import { IConsignment } from '@src/entities/consignment';

import { IMarking } from '@entities/marking';
import { IBoxGroup } from '@entities/box-group';
import { IUser } from '@entities/user';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';
import { IUserFilter } from '@entities/user-filter';

import { Permission, hasPermission } from '@src/enums/permission';
import { UserType } from '@enums/user-type';

import { HoldIcon } from '@core/icons';

const dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc');
dayjs.extend(utc);

interface IOnHoldParams {
    onHoldReason?: string;
}

const Consignment = () => {
    const [modal, modalContextHolder] = Modal.useModal();

    const navigate = useNavigate();

    const [onHoldForm] = Form.useForm();

    const { TextArea } = Input;
    const [api, contextHolder] = notification.useNotification();

    const { carrierId, consignmentId } = useParams();

    const [form] = Form.useForm();
    const userId = Form.useWatch<string>('userId', form);

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [carrier, setCarrier] = useState<IInboundCarrier>();
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);
    const [users, setUsers] = useState<Array<IUser>>([]);
    const [markings, setMarkings] = useState<Array<IMarking>>([]);

    const [boxGroups, setBoxGroups] = useState<Array<IBoxGroup>>([]);

    const [currentBoxGroup, setCurrentBoxGroup] = useState<IBoxGroup>();

    const [entity, setEntity] = useState<IConsignment>({ id: undefined, carrierId: carrierId, userId: undefined, markingId: undefined });

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [onHoldOpen, setOnHoldOpen] = useState<boolean>(false);

    const [manageTruckConsignment] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment));

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [onHoldProcessing, setOnHoldProcessing] = useState<boolean>(false);

    useEffect(() => {
        serverFetch(`inboundcarriers/${carrierId}`, { method: 'GET' })
            .then((data) => {
                setCarrier(data);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения рейса', ex, () => d(userLoaded(undefined)));
            });
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const userFilter: IUserFilter = { types: [UserType.MainClient], isActive: true, isArchived: false };

            const promises = [
                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('countries/items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (consignmentId) {
                promises.push(
                    await serverFetch(`consignments/${consignmentId}/import`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUsers(result[0][0]);

                let loadedCountries: Array<ICountry> = result[0][1].filter(
                    (c: ICountry) => c.generateTariff && c.airportCode == carrier?.departurePoint,
                );
                setCountryItems(result[0][2]);

                let data: IConsignment = result[0][3] || { ...entity };

                var entities: Array<IBoxGroup> = [];

                loadedCountries.map((c: ICountry) => {
                    let countryBoxGroups = data.boxGroups?.filter((b) => b.countryId === c.id);

                    if (countryBoxGroups && countryBoxGroups.length > 0) {
                        countryBoxGroups?.map((bg) => {
                            form.setFieldsValue({
                                cityId: bg.cityId,
                            });

                            entities.push({
                                ...bg,
                                key: `${bg.countryId}#${bg.itemId}#${bg?.awbNumber}`,
                                countryId: c.id,
                                countryName: c.name,
                                countryCode: c.code,
                            });
                        });
                    }
                });

                setCountries(loadedCountries);

                setBoxGroups(entities);

                if (consignmentId) {
                    form.setFieldsValue({
                        id: data.id,
                        carrierId: data.carrierId,
                        userId: data.userId,
                        markingId: data.markingId,
                        cityId: data.cityId,
                        comment: data.comment,
                    });
                }

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        setMarkings([]);

        if (!userId) return;

        serverFetch('markings', { method: 'GET', queryParams: { userId: userId } })
            .then((data) => {
                setMarkings(data);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения маркировок', ex, () => d(userLoaded(undefined)));
            });
    }, [userId]);

    useEffect(() => {
        if (!markings || markings.length != 1) return;

        form.setFieldsValue({
            markingId: markings[0].id,
            cityId: markings[0].cityId,
        });
    }, [markings]);

    const onMarkingChanged = async (value: string | undefined) => {
        let marking = markings.find((m) => m.id == value);
        if (!marking) return;

        form.setFieldsValue({
            markingId: value,
            cityId: marking.cityId,
        });
    };

    const onFinish = (data: IConsignment) => {
        let filledBoxGroups = boxGroups.filter((bg) => bg.qty && bg.qty > 0);
        if (filledBoxGroups.length <= 0) {
            warning(api, 'Хотя бы одна партия должна быть заполнена');
            return;
        }

        const duplicate = filledBoxGroups.find((bg) => {
            const duplicate = filledBoxGroups.find(
                (d) => d.key != bg.key && d.countryId == bg.countryId && d.itemId == bg.itemId && d.awbNumber == bg.awbNumber,
            );
            return !!duplicate;
        });

        if (duplicate) {
            error(api, `Удалите дублирующиеся позиции в разделе "${duplicate.countryName}"`);
            return;
        }

        filledBoxGroups.map((bg) => {
            bg.cityId = data.cityId;
        });

        let result = { ...entity, ...data, boxGroups: filledBoxGroups };

        setSaving(true);

        serverFetch(`consignments`, { method: consignmentId ? 'PUT' : 'POST', bodyData: result })
            .then(() => {
                setSaving(false);
                navigate(-1);
            })
            .catch((ex) => {
                setSaving(false);
                exception(api, 'Ошибка сохранения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetOnHold = (entity: IOnHoldParams) => {
        if (!currentBoxGroup) return;

        setOnHoldProcessing(true);

        serverFetch(`remainings/onhold`, {
            method: 'POST',
            bodyData: {
                ids: [currentBoxGroup.id],
                onHoldReason: entity.onHoldReason,
            },
        })
            .then(() => {
                setOnHoldProcessing(false);

                onHoldForm.resetFields();
                setCurrentBoxGroup(undefined);
                setOnHoldOpen(false);

                setRefreshRequired(true);
            })
            .catch((ex) => {
                setOnHoldProcessing(false);
                exception(api, 'Ошибка блокировки грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onAddBoxGroup = (countryId: string | undefined) => {
        if (!countryId) return;

        let country = countries.find((c) => c.id == countryId);
        if (!country) return;

        let groups = [...boxGroups];

        groups.push({
            key: uuid(),
            id: undefined,
            consignmentId: consignmentId,
            countryId: countryId,
            countryName: country.name,
            countryCode: country.code,
            itemId: undefined,
            qty: undefined,
            calcVolume: undefined,
            loadingOn: undefined,
            awbNumber: undefined,
        });

        setBoxGroups(groups);
    };

    const onDeleteBoxGroup = (key: string | undefined) => {
        if (!key) return;

        let groups = [...boxGroups];
        groups = groups.filter((g) => g.key != key);

        setBoxGroups(groups);
    };

    const renderBoxGroupsForm = () => {
        let groups = [...boxGroups];
        return countries.map((c) => {
            let countryGroups = groups.filter((g) => g.countryId == c.id);
            return (
                <Form.Item key={c.id}>
                    <Divider titlePlacement='left' style={{ marginTop: 0 }}>
                        <Tooltip title='Добавить партию' placement='topLeft'>
                            <Button
                                size='small'
                                icon={<PlusOutlined />}
                                style={{ margin: '4px 15px 2px -5px' }}
                                onClick={() => {
                                    onAddBoxGroup(c.id);
                                }}
                            />
                            {c.name}
                        </Tooltip>
                    </Divider>

                    {countryGroups.map((bg: IBoxGroup) => {
                        return (
                            <Form.Item key={bg.key} labelCol={{ span: 2 }} wrapperCol={{ span: 22 }}>
                                <Flex gap='middle' align='center'>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Button
                                            icon={<DeleteOutlined />}
                                            disabled={bg.hasBill}
                                            onClick={() => {
                                                modal.confirm({
                                                    title: `Удалить выбранную партию?`,
                                                    icon: <QuestionCircleOutlined />,
                                                    okType: 'primary',
                                                    okText: 'ОК',
                                                    cancelText: 'Отмена',
                                                    onOk: () => {
                                                        onDeleteBoxGroup(bg.key);
                                                    },
                                                });
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name={`${bg.key}itemId`}
                                        initialValue={bg.itemId}
                                        style={{ width: 180, minWidth: 180, marginBottom: 0 }}
                                        rules={[{ required: true, message: 'Укажите продукт' }]}
                                    >
                                        <Select
                                            placeholder='Продукт'
                                            disabled={!manageTruckConsignment}
                                            onChange={(value) => {
                                                let boxGroup = groups.find((d) => d.key === bg.key);
                                                if (boxGroup) {
                                                    boxGroup.itemId = value;
                                                }
                                            }}
                                            filterOption={(input, option) =>
                                                (option?.label as string).toLowerCase().startsWith(input.toLowerCase())
                                            }
                                            options={countryItems
                                                .filter((i) => i.countryId == bg.countryId)
                                                .map((i) => {
                                                    return { value: i.itemId, label: i.itemName };
                                                })}
                                        />
                                    </Form.Item>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space.Compact>
                                            <Form.Item initialValue={bg.qty} name={`${bg.key}qty`} style={{ marginBottom: 0 }}>
                                                <InputNumber
                                                    style={{ width: 100, minWidth: 100, marginBottom: 0 }}
                                                    placeholder='Количество'
                                                    disabled={!manageTruckConsignment}
                                                    min={0}
                                                    onChange={(value: number | null) => {
                                                        let boxGroup = groups.find((d) => d.key === bg.key);
                                                        if (boxGroup) {
                                                            boxGroup.qty = value;
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                            <Space.Addon>шт</Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space.Compact>
                                            <Form.Item
                                                initialValue={bg.grossWeight}
                                                name={`${bg.key}grossWeight`}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    style={{ width: 100, minWidth: 100, marginBottom: 0 }}
                                                    decimalSeparator=','
                                                    min={0}
                                                    placeholder='Брутто'
                                                    disabled={!manageTruckConsignment}
                                                    stringMode
                                                    step={1}
                                                    onChange={(value: number | null) => {
                                                        let boxGroup = groups.find((d) => d.key === bg.key);
                                                        if (boxGroup) {
                                                            boxGroup.grossWeight = value;
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                            <Space.Addon>кг</Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space.Compact>
                                            <Form.Item
                                                initialValue={bg.volumeWeight}
                                                name={`${bg.key}volumeWeight`}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    style={{ width: 100, minWidth: 100, marginBottom: 0 }}
                                                    decimalSeparator=','
                                                    placeholder='Объемный'
                                                    min={0}
                                                    disabled={!manageTruckConsignment}
                                                    stringMode
                                                    step={1}
                                                    onChange={(value: number | null) => {
                                                        let boxGroup = groups.find((d) => d.key === bg.key);
                                                        if (boxGroup) {
                                                            boxGroup.volumeWeight = value;
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                            <Space.Addon>кг</Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                    <Form.Item
                                        name={`${bg.key}awbNumber`}
                                        initialValue={bg.awbNumber}
                                        style={{ width: 250, minWidth: 250, marginBottom: 0 }}
                                    >
                                        <Input
                                            placeholder='Номер AWB'
                                            disabled={!manageTruckConsignment}
                                            onChange={(value: React.ChangeEvent<HTMLInputElement>) => {
                                                let boxGroup = groups.find((d) => d.key === bg.key);
                                                if (boxGroup) {
                                                    boxGroup.awbNumber = value.target.value;
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    {bg.id && (
                                        <Tooltip title={bg.onHold ? 'Разблокировать' : 'Заблокировать'}>
                                            <Button
                                                style={{ width: 35, minWidth: 35, color: bg.onHold ? 'darkorchid' : '' }}
                                                icon={<HoldIcon />}
                                                onClick={() => {
                                                    if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                                        securityRestriction(api, [Permission.ManageWarehouse]);
                                                        return;
                                                    }

                                                    setCurrentBoxGroup(bg);

                                                    setOnHoldOpen(true);
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Flex>
                            </Form.Item>
                        );
                    })}
                </Form.Item>
            );
        });
    };

    return (
        <>
            <FormHeader title={`${consignmentId ? 'Изменить партию' : 'Добавить партию'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 16 }} form={form} onFinish={onFinish}>
                    <Form.Item name='id' hidden={true}>
                        <Input />
                    </Form.Item>
                    <Form.Item name='cityId' hidden={true}>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        wrapperCol={{ span: 6 }}
                        initialValue={entity.markingId}
                        required
                        label='Клиент'
                        name='userId'
                        rules={[{ required: true, message: 'Выберите клиента' }]}
                    >
                        <Select
                            disabled={!manageTruckConsignment}
                            autoFocus
                            onChange={(value) => form.setFieldsValue({ userId: value })}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={users.map((u) => {
                                return { value: u.id, label: ` ${u.login}` };
                            })}
                        />
                    </Form.Item>

                    <Form.Item
                        wrapperCol={{ span: 6 }}
                        initialValue={entity.markingId}
                        required
                        label='Маркировка'
                        name='markingId'
                        rules={[{ required: true, message: 'Выберите маркировку' }]}
                    >
                        <Select
                            disabled={!manageTruckConsignment || !userId}
                            onChange={(value) => onMarkingChanged(value)}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={markings.map((m) => {
                                return { value: m.id, label: `${m.code} / ${m.cityName}` };
                            })}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ span: 16 }} initialValue={entity.comment} label='Комментарий' name='comment'>
                        <TextArea
                            rows={3}
                            disabled={!manageTruckConsignment}
                            onChange={(data) => {
                                setEntity({ ...entity, comment: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 30, marginBottom: 0 }} wrapperCol={{ offset: 1, span: 18 }}>
                        {renderBoxGroupsForm()}
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 1, span: 18 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Закрыть
                            </Button>
                            {manageTruckConsignment && (
                                <Button type='primary' htmlType='submit' loading={loading || saving}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            <Modal
                title={
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'start' }}>
                        <QuestionCircleOutlined style={{ color: '#faad14', marginInlineEnd: 12, fontSize: 22 }} />
                        {currentBoxGroup?.onHold
                            ? `Разблокировать '${currentBoxGroup.countryName}'?`
                            : `Заблокировать '${currentBoxGroup?.countryName}'?`}
                    </div>
                }
                open={onHoldOpen}
                okText='Сохранить'
                onOk={() => onHoldForm.submit()}
                onCancel={() => {
                    onHoldForm.resetFields();
                    setCurrentBoxGroup(undefined);
                    setOnHoldOpen(false);
                }}
                closable={false}
                cancelButtonProps={{ style: { display: onHoldProcessing ? 'none' : '' } }}
                confirmLoading={onHoldProcessing}
            >
                {!currentBoxGroup?.onHold && (
                    <Form
                        colon={false}
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 18 }}
                        onFinish={onSetOnHold}
                        form={onHoldForm}
                        style={{ marginTop: 10 }}
                    >
                        <Form.Item
                            required={true}
                            label='Причина'
                            name='onHoldReason'
                            rules={[{ required: true, message: 'Укажите причину блокировки' }]}
                        >
                            <TextArea autoFocus rows={4} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Consignment;
